import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ScoreMeter from "../components/ScoreMeter";
import VibeTag from "../components/VibeTag";
import ProgressionIndicator from "../components/ProgressionIndicator";
import { roundAmbience } from "../components/roundAmbience";
import { apiFetch } from "../lib/api";

export default function TournamentDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [appealMsg,       setAppealMsg]       = useState("");
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  const [appealDone,      setAppealDone]      = useState(false);
  const [appealError,     setAppealError]     = useState("");

  useEffect(() => {
    apiFetch("/user/state")
      .then(setData)
      .catch(() => {});
  }, []);

  const submitAppeal = async () => {
    if (!appealMsg.trim()) { setAppealError("Please describe your situation"); return; }
    setAppealSubmitting(true); setAppealError("");
    try {
      const token = localStorage.getItem("bm_token") || localStorage.getItem("token") || "";
      const res = await fetch("/api/appeal", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: appealMsg }),
      });
      const d = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(d.detail || "Submission failed");
      setAppealDone(true);
    } catch (e) { setAppealError(e.message); }
    finally { setAppealSubmitting(false); }
  };

  const seven = data?.prospects || [];
  // TEMP FIX: no automatic slicing — wait for real round results
  const advanced = [];
  const paused = [];
  const current = data?.currentRound || {};
  const roundsMeta = data?.roundsMeta || [];
  const ambience = roundAmbience(current?.kind ?? "chemistry");

  const currentRoundLabel = current?.name || "Your Journey";
  const advancingCount = Math.min(seven.length, 5);
  const pausedCount = Math.max(seven.length - 5, 0);
  const roundsRemaining = Math.max(0, 4 - roundsMeta.length);

  const hasProspects = seven.length > 0;
  const hasAdvanced = advanced.length > 0;
  const hasPaused = paused.length > 0;
  const hasRounds = roundsMeta.length > 0;


  if (data && data.account_status === "disabled") {
    return (
      <AppShell>
        <div className="min-h-[70vh] flex items-center justify-center px-6 py-20">
          <div className="glass-strong rounded-3xl p-10 max-w-md w-full text-center">
            <div className="text-4xl mb-5">⚠</div>
            <div className="text-[11px] tracking-[0.24em] uppercase text-red-400 mb-3">Account Disabled</div>
            <h1 className="font-display text-3xl mb-4">Your account has been disabled</h1>
            <p className="text-white/55 text-sm mb-8">
              Our safety team has disabled your account. If you believe this was a mistake,
              submit an appeal below. We review all appeals within 48 hours.
            </p>
            {appealDone ? (
              <div className="glass rounded-2xl p-6">
                <div className="text-2xl mb-3">✓</div>
                <div className="font-semibold text-emerald-400 mb-2">Appeal submitted</div>
                <p className="text-white/50 text-sm">We'll review your appeal and respond within 48 hours.</p>
              </div>
            ) : (
              <div className="text-left">
                {appealError && <p className="text-red-400 text-sm mb-3">{appealError}</p>}
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/40 block mb-2">Your appeal message</label>
                <textarea
                  value={appealMsg}
                  onChange={(e) => setAppealMsg(e.target.value)}
                  placeholder="Explain why you believe your account should be reinstated…"
                  rows={5}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 outline-none resize-none mb-4"
                />
                <button
                  onClick={submitAppeal}
                  disabled={appealSubmitting || !appealMsg.trim()}
                  className="w-full rounded-xl bg-gradient-gold text-[#080B14] font-semibold py-3 text-sm glow-gold-soft disabled:opacity-40"
                >
                  {appealSubmitting ? "Submitting…" : "Submit Appeal"}
                </button>
              </div>
            )}
          </div>
        </div>
      </AppShell>
    );
  }

  if (data && !hasProspects) {
    return (
      <AppShell>
        <div className="min-h-[70vh] flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <h1 className="font-display text-4xl mb-4">Start your journey</h1>
            <p className="text-white/55 mb-8">Choose your Select Seven to begin</p>
            <Link
              to="/select-seven"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold-soft"
            >
              Choose Select Seven
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14">
        {/* Hero status */}
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 mb-10">
          <div className="glass-strong rounded-3xl p-8 relative overflow-hidden grain">
            <div
              className="absolute inset-0 pointer-events-none"
              style={ambience.glowStyle}
            />
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
              {currentRoundLabel} · now
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.02]">
              {current?.name ?? "Chemistry Round"}
            </h1>
            <p className="text-white/65 mt-3 max-w-lg">
              {current?.subtitle} A 20-second prompt each. No retakes. The ones
              who feel most themselves advance.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {[
                `${advancingCount} still advancing`,
                `${pausedCount} paths closed`,
                `${roundsRemaining} more rounds to go`,
              ].map((l, i) => (
                <VibeTag
                  key={l}
                  tone={i === 0 ? "forest" : i === 1 ? "burgundy" : "default"}
                >
                  {l}
                </VibeTag>
              ))}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/round"
                data-testid="continue-round"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold-soft"
              >
                Resume round →
              </Link>
              <Link
                to="/bracket"
                data-testid="see-bracket"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/35 px-6 py-3 text-white/85"
              >
                See bracket
              </Link>
            </div>

            {/* progression indicator */}
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mb-3">
                The journey · 7 → 1
              </div>
              <ProgressionIndicator variant="strong" current={2} showCaption={false} />
            </div>
          </div>

          {/* Stats column */}
          <div className="grid gap-5">
            <div className="glass rounded-3xl p-6">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-4">
                Highlights
              </div>
              <div className="space-y-5">
                {[
                  { k: "Strongest connection", v: data?.you?.strongestMatch, t: "", tone: "gold" },
                  { k: "Highest effort", v: data?.you?.highestEffort, t: "", tone: "forest" },
                  { k: "Biggest surprise", v: data?.you?.surprisePick, t: "", tone: "burgundy" },
                ].map((s) => (
                  <div key={s.k} className="flex items-center gap-4">
                    <div
                      className={`h-10 w-10 rounded-full grid place-items-center text-[10px] uppercase tracking-[0.2em] ${
                        s.tone === "gold"
                          ? "bg-[rgba(212,175,55,0.1)] text-[#F2DE94] ring-1 ring-[rgba(212,175,55,0.35)]"
                          : s.tone === "forest"
                            ? "bg-[rgba(74,163,106,0.12)] text-[#8FD6A6] ring-1 ring-[rgba(74,163,106,0.35)]"
                            : "bg-[rgba(185,58,80,0.12)] text-[#F5A8B5] ring-1 ring-[rgba(185,58,80,0.35)]"
                      }`}
                    >
                      {(s.v || "—").slice(0,1)}
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{s.k}</div>
                      <div className="font-display">{s.v}</div>
                    </div>
                    <div className="text-xs text-white/60">{s.t}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass rounded-3xl p-6">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-4">
                Overall compatibility
              </div>
              {data?.compatibility_breakdown ? (
                <>
                  <ScoreMeter label="Values" value={data.compatibility_breakdown.values ?? 0} tone="gold" />
                  <div className="h-3" />
                  <ScoreMeter label="Communication" value={data.compatibility_breakdown.communication ?? 0} tone="gold" />
                  <div className="h-3" />
                  <ScoreMeter label="Chemistry" value={data.compatibility_breakdown.chemistry ?? 0} tone="burgundy" />
                  <div className="h-3" />
                  <ScoreMeter label="Future goals" value={data.compatibility_breakdown.future_goals ?? 0} tone="forest" />
                </>
              ) : (
                <p className="text-white/40 text-sm">Complete your rounds to unlock insights.</p>
              )}
            </div>
          </div>
        </div>

        {/* Advancing cards */}
        {hasAdvanced && <div className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl">Still advancing</h2>
            <div className="text-xs text-white/45">Ranked by round score</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {advanced.map((p, i) => {
              // Tension: the remaining profiles feel more important as the
              // round tightens. Top ranks glow gold; lower ranks fade gently.
              const tensionRing =
                i === 0
                  ? "ring-2 ring-[rgba(212,175,55,0.55)] glow-gold"
                  : i === 1
                    ? "ring-1 ring-[rgba(212,175,55,0.35)] glow-gold-soft"
                    : "ring-1 ring-[rgba(74,163,106,0.35)]";
              const dim = i >= 3 ? "opacity-85" : "";
              return (
              <div
                key={p.id}
                className={`relative rounded-2xl overflow-hidden aspect-[3/4] group transition-all duration-500 ${tensionRing} ${dim}`}
              >
                <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/20 to-transparent" />
                <span className="absolute top-2 left-2 h-6 w-6 rounded-full bg-gradient-gold text-[#05070d] grid place-items-center font-semibold text-[10px]">
                  {i + 1}
                </span>
                <span className="absolute top-2 right-2 glass rounded-full px-2 py-0.5 text-[11px] font-mono">
                  {p.compatibility}%
                </span>
                <div className="absolute bottom-3 inset-x-3">
                  <div className="font-display text-lg leading-tight">{p.name}</div>
                  <div className="text-[11px] text-[#8FD6A6] uppercase tracking-[0.18em]">
                    Advanced
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>}

        {/* Paused + round history */}
        <div className="grid md:grid-cols-2 gap-6">
          {hasPaused && (
          <div className="glass rounded-3xl p-6">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
              Path closed · gently
            </div>
            <div className="font-display text-2xl mb-4">Recently paused</div>
            <div className="space-y-3">
              {paused.map((p) => (
                <div key={p.id} className="flex items-center gap-4 rounded-xl bg-white/[0.03] p-3">
                  <img src={p.image} alt={p.name} className="h-12 w-12 rounded-xl object-cover opacity-60" />
                  <div className="flex-1">
                    <div className="font-display">{p.name}</div>
                    <div className="text-xs text-white/45">
                      Paused after {p === paused[0] ? "Communication Round" : "Values Round"} · {p.compatibility}%
                    </div>
                  </div>
                  <button className="text-xs text-white/55 hover:text-white rounded-full border border-white/10 px-3 py-1.5" data-testid={`revisit-${p.id}`}>
                    Revisit
                  </button>
                </div>
              ))}
            </div>
          </div>
          )}

          <div className="glass rounded-3xl p-6">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
              Round history
            </div>
            <div className="font-display text-2xl mb-4">Chapters so far</div>
            <div className="space-y-3">
              {roundsMeta.map((r, i) => (
                <div
                  key={r.id}
                  className={`flex items-start gap-4 rounded-xl p-3 ${
                    r.current ? "bg-[rgba(212,175,55,0.06)] ring-1 ring-[rgba(212,175,55,0.3)]" : "bg-white/[0.02]"
                  }`}
                >
                  <div
                    className={`h-8 w-8 rounded-full grid place-items-center font-mono text-[11px] ${
                      r.done ? "bg-[rgba(74,163,106,0.15)] text-[#8FD6A6]" : r.current ? "bg-gradient-gold text-[#05070d]" : "bg-white/5 text-white/40"
                    }`}
                  >
                    0{i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-display">{r.name}</div>
                    <div className="text-xs text-white/55">{r.summary}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
